import {
  createInstallationPresentation,
  parseInstallationPresentation,
} from 'ui.shipaid.com/renderers/checkout-plus-legacy';
import type { InstallationDraft, InstallationPreferencesV2 } from 'types/installationPresentation';
import { parseInstallationPreferences } from './installationPreferences';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export function parseInstallationDraft(value: unknown): InstallationDraft | null {
  try {
    if (!isRecord(value)) return null;
    // Keep the historical projecting v1 reader; extras are deliberately compatible.
    if (value.schemaVersion === 1) return parseInstallationPreferences(value);
    const keys = ['schemaVersion', 'status', 'payer', 'cartExperience', 'addToCart',
      'checkoutExtension', 'installationMethod', 'placement', 'appearance'];
    if (value.schemaVersion !== 2 || Object.keys(value).length !== keys.length ||
      !keys.every(key => Object.prototype.hasOwnProperty.call(value, key))) return null;
    const base = parseInstallationPreferences({ ...value, schemaVersion: 1,
      appearance: { mode: 'preserve-existing' } });
    const appearance = parseInstallationPresentation(value.appearance);
    return base && appearance ? { ...base, schemaVersion: 2, appearance } : null;
  } catch { return null; }
}

// Explicit local transformation only. The existing persistence controller is untouched.
export function upgradeInstallationPreferences(value: unknown): InstallationPreferencesV2 | null {
  try {
    const base = parseInstallationPreferences(value);
    if (!base) return null;
    return { ...base, schemaVersion: 2,
      appearance: createInstallationPresentation(base.appearance.mode === 'inherit-theme'
        ? 'match-theme' : 'preserve-existing') };
  } catch { return null; }
}

export function resetInstallationPresentation(value: unknown): InstallationPreferencesV2 | null {
  const draft = parseInstallationDraft(value);
  return draft?.schemaVersion === 2 ? { ...draft, appearance: createInstallationPresentation() } : null;
}
